import React from "react";
import type { MapPin, JournalEntry } from "@roll1/shared";
import type { UsePinsReturn } from "../hooks/usePins";
import { IconButton } from "./ui/icon-button";
import { ListRow } from "./ui/row";
import { useTokenContext } from "../contexts/TokenContext";
import { useSessionContext } from "../contexts/SessionContext";
import { useJournalContext } from "../contexts/JournalContext";
import { useCanvasInteraction } from "../contexts/CanvasInteractionContext";
import { Pencil, Trash2, FileText, Pin, Plus, Lock, UserRound } from "lucide-react";
import { useI18n } from "../i18n";
import SidebarPanel from "./ui/SidebarPanel";

const PIN_ICONS: Record<string, string> = {
  pin: "\u{1F4CC}",
  scroll: "\u{1F4DC}",
  skull: "\u{1F480}",
  chest: "\u{1F4E6}",
  star: "\u2B50",
  question: "\u2753",
  exclamation: "\u2757",
  camp: "\u26FA",
  sword: "\u2694\uFE0F",
  key: "\u{1F511}",
};

interface PinsPanelProps {
  pins?: MapPin[];
  isGM?: boolean;
  participantId?: string;
  pinsHook?: UsePinsReturn;
  pageId: string;
  onViewPin?: (pin: MapPin, entry?: JournalEntry) => void;
  onEditPin?: (pin: MapPin) => void;
}

const PinsPanel = React.memo(function PinsPanel({
  pins: pinsProp,
  isGM: isGMProp,
  participantId,
  pinsHook: pinsHookProp,
  pageId,
  onViewPin,
  onEditPin,
}: PinsPanelProps) {
  const tokenCtx = useTokenContext();
  const sessionCtx = useSessionContext();
  const journal = useJournalContext();
  const { get } = useCanvasInteraction();
  const { t } = useI18n();

  const pins = pinsProp ?? tokenCtx.pins.pins;
  const isGM = isGMProp ?? sessionCtx.isGM;
  const pinsHook = pinsHookProp ?? tokenCtx.pins;
  // H-13: a player's «only me» pin is theirs — the list knows who «me» is without being told.
  const myId = participantId ?? sessionCtx.mp.participant?.id;

  const visiblePins = isGM
    ? pins
    : pins.filter(
        (p) =>
          p.visibility === "all" ||
          (p.visibility === "owner" && p.createdBy === myId),
      );

  return (
    <SidebarPanel
      key={isGM ? "gm" : visiblePins.length === 0 ? "empty" : "listed"}
      id="pins"
      title={t('sb.pins')}
      count={visiblePins.length}
      icon={Pin}
      tight
      // A player who has pins in the list (their own «only me» pin included) sees the list open; the GM's panel starts folded.
      defaultCollapsed={isGM || visiblePins.length === 0}
      actions={isGM ? (
        <IconButton label={t('sb.map.addPin')} icon={<Plus size={14} aria-hidden="true" />} onClick={() => get("pin:quick-create")?.()} />
      ) : undefined}
    >
      {visiblePins.length === 0 ? (
        <p className="sb-line py-1 text-meta text-dim">{t('pins.empty')}</p>
      ) : (
        <ul className="flex flex-col">
          {visiblePins.map((pin) => {
            const entry = pin.journalEntryId
              ? journal.entries.find((e) => e.id === pin.journalEntryId)
              : undefined;
            const mayEdit = isGM || pin.createdBy === myId;
            return (
              <ListRow
                key={pin.id}
                data-testid={`pin-item-${pin.id}`}
                lead={PIN_ICONS[pin.icon] ?? "📌"}
                label={entry?.name ?? t('pins.orphan')}
                onOpen={() => onViewPin?.(pin, entry)}
                deco={[
                  ...(isGM && pin.visibility === "gm" ? [{ icon: <Lock size={12} aria-hidden="true" />, label: t('sb.gmOnly') }] : []),
                  ...(isGM && pin.visibility === "owner" ? [{ icon: <UserRound size={12} aria-hidden="true" />, label: t('pins.ownerOnly') }] : []),
                ]}
                meta={entry ? t(`journal.type.${entry.type}` as never) : !pin.journalEntryId ? <span className="text-warn">{t('pins.noEntry')}</span> : undefined}
                actions={mayEdit ? (
                  <IconButton
                    size="sm"
                    label={t('sb.pin.edit')}
                    icon={<Pencil size={14} aria-hidden="true" />}
                    onClick={() => {
                      const editHandler = get("pin:edit");
                      if (editHandler) editHandler(pin);
                      else onEditPin?.(pin);
                    }}
                  />
                ) : undefined}
                menu={[
                  { label: t('sb.pin.open'), icon: FileText, onSelect: () => onViewPin?.(pin, entry), hidden: onViewPin === undefined },
                  { separator: true },
                  {
                    label: t('sb.delete'),
                    icon: Trash2,
                    danger: true,
                    hidden: !mayEdit,
                    confirm: { title: t('sb.pin.deleteTitle'), body: t('sb.pin.deleteBody'), yes: t('sb.deleteYes') },
                    onSelect: () => pinsHook.deletePin(pageId, pin.id),
                  },
                ]}
              />
            );
          })}
        </ul>
      )}
    </SidebarPanel>
  );
});

export default PinsPanel;
